import React, { useEffect, useState } from "react";
import {
    StyledImage, StyledFileIcon,
    FileAttachment, ClickableResendText,
    Bubble, BubbleAndActionContainerElement,
    QueuedMessageWrapper, PlayIconOverlay
} from "./styled";
import { extractVideoThumbnail, mxcToHttp } from "../utils";
import FileIcon from "src/img/notice/document.svg";
import { PlayIcon } from "src/components";

const QueuedMessagePreview = React.memo(
    ({ queuedMessage, resendMessage, baseUrl, originalEvent }) => {
        if (!queuedMessage) return null;
        const currentFile = queuedMessage.message?.file;
        const fileTypePrefix = currentFile?.type?.split("/")[0];
        const isImageOrVideo = fileTypePrefix === "image" || fileTypePrefix === "video";

        const [previewUrl, setPreviewUrl] = useState(null);

        const fallbackUrl =
            queuedMessage?.status === "failed" &&
            typeof queuedMessage?.message?.url === "string" &&
            queuedMessage.message.url.startsWith("mxc://")
                ? mxcToHttp(queuedMessage.message.url, baseUrl)
                : null;

        useEffect(() => {
            let revokeUrl = null;

            const generatePreview = async () => {
                setPreviewUrl(null);
                try {
                    const fileType = currentFile?.type || "";
                    const typePrefix = fileType.split("/")[0];

                    if (typePrefix === "image" || (queuedMessage.status === "failed" && fallbackUrl?.match(/\.(jpg|jpeg|png|gif|webp)/))) {
                        if (fallbackUrl) {
                            setPreviewUrl(fallbackUrl);
                        } else if (currentFile) {
                            const url = URL.createObjectURL(currentFile);
                            setPreviewUrl(url);
                            revokeUrl = url;
                        }
                    } else if (typePrefix === "video") {
                        const source = fallbackUrl || currentFile;
                        let thumb = queuedMessage.message?.info?.thumbnail_url
                            ? mxcToHttp(queuedMessage.message.info.thumbnail_url, baseUrl)
                            : null;

                        if (!thumb && source) {
                            thumb = await extractVideoThumbnail(source, { asBlob: false });
                        }

                        if (thumb) setPreviewUrl(thumb);
                    }
                } catch (err) {
                    console.error("Preview generation error:", err);
                }
            };

            generatePreview();

            return () => {
                if (revokeUrl) URL.revokeObjectURL(revokeUrl);
            };
        }, [currentFile, fallbackUrl, queuedMessage]);

        if (!queuedMessage?.isFile && !queuedMessage?.message) return null;

        const renderStatusText = () => {
            if (queuedMessage.status === "failed") {
                const reason =
                    queuedMessage.failureSource === "uploadContent"
                        ? "Upload failed"
                        : queuedMessage.failureSource === "sendAttachment"
                            ? "Attachment failed"
                            : queuedMessage.failureSource === "sendMessage"
                                ? "Message not delivered"
                                : "Failed to send";

                return (
                    <div style={{ color: "#C41E3A", fontSize: "0.750rem", marginTop: "0.25rem" }}>
                        {reason}&nbsp;
                        <ClickableResendText onClick={() => resendMessage?.(originalEvent)}>Resend?</ClickableResendText>
                    </div>
                );
            }

            if (queuedMessage.status === "sending") {
                return (
                    <p style={{ color: "gray", fontSize: "0.750rem", marginTop: "0.25rem" }}>
                        Sending message...
                    </p>
                );
            }

            return null;
        };

        return (
            <QueuedMessageWrapper>
                {/* Text messages */}
                <BubbleAndActionContainerElement isChatByCurrentUser={true}>
                    {queuedMessage.msgType === "m.text" && typeof queuedMessage.message === "string" && (
                        <Bubble
                            isChatByCurrentUser={true}
                            isMediaMessage={false}
                            isFileMessage={false}
                            style={{ borderRadius: "10px 0px 10px 10px" }}
                            $failed={queuedMessage.status === "failed"}
                        >
                            {queuedMessage.message}
                        </Bubble>
                    )}
                </BubbleAndActionContainerElement>

                {/* File messages (image/video/file) */}
                {queuedMessage?.isFile && currentFile && (
                    <>
                        {isImageOrVideo && previewUrl && (
                            <>
                                {fileTypePrefix === "image" ? (
                                    <StyledImage
                                        src={previewUrl}
                                        alt={previewUrl}
                                        maxWidth="12rem"
                                        maxHeight="12rem"
                                        $uploading={queuedMessage.status === "sending"}
                                        $failed={queuedMessage.status === "failed"}
                                    />
                                ) : (
                                    <Bubble isMediaMessage={true} isFileMessage={false}>
                                        <StyledImage
                                            src={previewUrl}
                                            preload="metadata"
                                            maxWidth="12rem"
                                            maxHeight="12rem"
                                            $uploading={queuedMessage.status === "sending"}
                                            $failed={queuedMessage.status === "failed"}
                                        />
                                        <PlayIconOverlay alt="Play Video" width="24px" height="18px">
                                            <PlayIcon/>
                                        </PlayIconOverlay>
                                    </Bubble>
                                )}
                            </>
                        )}

                        {!isImageOrVideo && (
                            <FileAttachment
                                $uploading={queuedMessage.status === "sending"}
                                $failed={queuedMessage.status === "failed"}
                            >
                                <StyledFileIcon src={FileIcon} alt="file" />
                                <span style={{ fontSize: "0.875rem" }}>{currentFile.name}</span>
                            </FileAttachment>
                        )}
                    </>
                )}

                {renderStatusText()}
            </QueuedMessageWrapper>
        );
    },
    (prevProps, nextProps) =>
        prevProps.queuedMessage?.message === nextProps.queuedMessage?.message &&
        prevProps.queuedMessage?.status === nextProps.queuedMessage?.status &&
        prevProps.queuedMessage?.msgType === nextProps.queuedMessage?.msgType &&
        prevProps.originalEvent?.getId?.() === nextProps.originalEvent?.getId?.()

);

export default QueuedMessagePreview;
